import { Input, Tooltip } from "@xola/ui-kit";
import { SwitchField } from "common";
import { isNil } from "lodash";
import React, { useState } from "react";
import { AvailabilityTimelinePreference } from "types";
import { NumberInlineValuePopover } from "../../../Common/NumberInlineValuePopover";

interface TimelinePreferencesFormProps {
    value: AvailabilityTimelinePreference;
    errors?: any;
    onChange: (value: AvailabilityTimelinePreference) => void;
}

export const TimelinePreferencesForm = ({ value, errors, onChange }: TimelinePreferencesFormProps) => {
    const hasOpenMax = !isNil(value.showOpenMax);
    const [showOpenMaxPopover, setShowOpenMaxPopover] = useState(false);

    const handleChange = (object: any) => {
        const currentValue = { ...value, ...object };
        onChange(currentValue);
    };

    const handleOpenMaxToggleChange = (enabled: boolean) => {
        handleChange({ showOpenMax: enabled ? "0" : null });
    };

    const handleShowUnavailableChange = (enabled: boolean) => {
        handleChange({ showUnavailable: enabled, showUnavailableMessage: false, unavailableMessage: null });
    };

    const handleShowUnavailableMessageChange = (value: boolean) => {
        handleChange({ showUnavailableMessage: value, unavailableMessage: null });
    };

    return (
        <div className="flex flex-col space-y-2">
            <SwitchField
                size="medium"
                isChecked={value.showPrice ?? false}
                onChange={(value) => handleChange({ showPrice: value })}
            >
                Price per person
            </SwitchField>
            <SwitchField
                size="medium"
                isChecked={value.showOpen ?? false}
                onChange={(value) => handleChange({ showOpen: value })}
            >
                Show the open seat count
            </SwitchField>
            {value.showOpen ? (
                <Tooltip content="Displaying availability when the open seat count is less than a threshold may instill 'Fear of missing out' in travelers">
                    <div className="ml-10">
                        <SwitchField size="medium" isChecked={hasOpenMax} onChange={handleOpenMaxToggleChange}>
                            <span onClick={(e) => e.stopPropagation()}>
                                Conditionally show the open seat count
                                {hasOpenMax ? (
                                    <span>
                                        <span> when count is less than or equal to</span>
                                        <NumberInlineValuePopover
                                            isOpen={showOpenMaxPopover}
                                            classNames={{ input: "w-30" }}
                                            text={`${value.showOpenMax}`}
                                            error={errors?.showOpenMax}
                                            value={Number.parseInt(value.showOpenMax ?? "0", 10)}
                                            onOpenChange={() => setShowOpenMaxPopover(!showOpenMaxPopover)}
                                            onChange={(openMax) => handleChange({ showOpenMax: openMax })}
                                        />
                                    </span>
                                ) : null}
                            </span>
                        </SwitchField>
                    </div>
                </Tooltip>
            ) : null}
            <SwitchField
                size="medium"
                isChecked={value.showUnavailable ?? false}
                onChange={(value) => handleShowUnavailableChange(value)}
            >
                Show unavailable date and time slots
            </SwitchField>
            {value.showUnavailable ? (
                <SwitchField
                    size="medium"
                    isChecked={value.showUnavailableMessage ?? false}
                    onChange={handleShowUnavailableMessageChange}
                >
                    Show a custom message for dates with 0 availability
                </SwitchField>
            ) : null}
            {value.showUnavailable && value.showUnavailableMessage ? (
                <div className="ml-10">
                    <Input
                        isError={errors?.unavailableMessage}
                        className="w-40"
                        placeholder="E.g. Sold Out"
                        name="unavailableMessage"
                        value={value.unavailableMessage}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange({ unavailableMessage: e.target.value })
                        }
                    />
                </div>
            ) : null}
            <SwitchField
                size="medium"
                isChecked={value.showExcerpt ?? false}
                onChange={(value) => handleChange({ showExcerpt: value })}
            >
                Display excerpt in timeline view
            </SwitchField>
        </div>
    );
};
